/**
 * Version state — holds the UI build stamp and the fetched API build stamp.
 *
 * UI version: sourced from the VITE_APP_VERSION build-time env var injected by CI.
 * API version: fetched at runtime from /api/health.version (the documented API surface).
 *
 * The UI must never collapse these two values; they are displayed separately per PRD.
 */
import { Signal } from 'signal-polyfill';
import { fetchHealth } from '../api/health.api';

export interface VersionState {
  /** Build stamp for the UI artifact, set at Vite build time. */
  uiVersion: string;
  /** Build stamp returned by /api/health.version; null until fetched or when unavailable. */
  apiVersion: string | null;
  /** True while the /api/health fetch is in flight. */
  apiVersionLoading: boolean;
  /**
   * True once a fetch attempt has completed (whether successful or not).
   * Prevents repeated background polling after a failure.
   */
  apiVersionFetched: boolean;
}

const uiVersion = import.meta.env.VITE_APP_VERSION ?? 'dev';

export const initialVersionState: VersionState = {
  uiVersion,
  apiVersion: null,
  apiVersionLoading: false,
  apiVersionFetched: false,
};

export const versionState = new Signal.State<VersionState>(initialVersionState);

let _fetchPromise: Promise<void> | null = null;

export const versionActions = {
  /**
   * Fetch the API version from /api/health once.
   * No-op if a fetch is already in-flight or has previously completed (success or failure).
   * After completion, apiVersionFetched is set to true to prevent further retries.
   */
  async fetchApiVersion(): Promise<void> {
    const current = versionState.get();
    if (current.apiVersionFetched || current.apiVersionLoading) {
      return;
    }
    if (_fetchPromise) {
      return _fetchPromise;
    }

    versionState.set({ ...current, apiVersionLoading: true });

    _fetchPromise = fetchHealth()
      .then((data) => {
        versionState.set({
          ...versionState.get(),
          apiVersion: data?.version ?? null,
          apiVersionLoading: false,
          apiVersionFetched: true,
        });
      })
      .catch(() => {
        versionState.set({
          ...versionState.get(),
          apiVersionLoading: false,
          apiVersionFetched: true,
        });
      })
      .finally(() => {
        _fetchPromise = null;
      });

    return _fetchPromise;
  },
};
